import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

// SETUP COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

// Primary color: #569cd3 (Blue)
// Creating lighter and darker shades based on this color
const PRIMARY = {
  lighter: '#D1E9FC', // Very light blue (tint)
  light: '#7DB8E5', // Light blue
  main: '#569cd3', // Main blue color
  dark: '#3A7BA8', // Darker blue
  darker: '#1F4F7A', // Darkest blue
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#fff',
};

const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: GREY[800],
};

const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff',
};

// Function to create palette for both light and dark modes
export function createPalette(mode = 'light') {
  const isLight = mode === 'light';

  // Ensure all color objects have required properties
  const ensureColorObject = (colorObj) => ({
    lighter: colorObj.lighter || colorObj.main,
    light: colorObj.light || colorObj.main,
    main: colorObj.main,
    dark: colorObj.dark || colorObj.main,
    darker: colorObj.darker || colorObj.main,
    contrastText: colorObj.contrastText || '#fff',
  });

  return {
    mode,
    common: { black: '#000', white: '#fff' },
    primary: ensureColorObject(PRIMARY),
    secondary: ensureColorObject(SECONDARY),
    info: ensureColorObject(INFO),
    success: ensureColorObject(SUCCESS),
    warning: ensureColorObject(WARNING),
    error: ensureColorObject(ERROR),
    // Add 'main' as an alias to primary for backward compatibility
    main: ensureColorObject(PRIMARY),
    grey: GREY,
    divider: isLight ? alpha(GREY[500], 0.24) : alpha(GREY[400], 0.2),
    text: {
      primary: isLight ? GREY[800] : '#E5E7EB',
      secondary: isLight ? GREY[600] : GREY[400],
      disabled: isLight ? GREY[500] : GREY[600],
      titles: isLight ? '#A8469B' : '#C794E8', // Page titles color - brighter purple for dark mode
    },
    background: {
      paper: isLight ? '#fff' : '#1E1E2E',
      default: isLight ? GREY[100] : '#161622',
      neutral: isLight ? GREY[200] : '#252535',
      // Legacy colors for backward compatibility
      secondary: '#389ED8',
      purple: isLight ? '#9470b1' : '#B894D4',
      light: isLight ? '#C6CBD3' : '#2A2A3C',
      green: isLight ? '#6B9D98' : '#8BBBB6',
      td: isLight ? '#5EC4C7' : '#7DD4D7',
    },
    action: {
      active: isLight ? GREY[600] : GREY[300],
      hover: isLight ? alpha(GREY[500], 0.08) : alpha('#9370DB', 0.12),
      selected: isLight ? alpha(PRIMARY.main, 0.16) : alpha('#9370DB', 0.18),
      disabled: isLight ? alpha(GREY[500], 0.8) : alpha(GREY[600], 0.6),
      disabledBackground: isLight ? alpha(GREY[500], 0.24) : alpha(GREY[600], 0.2),
      focus: isLight ? alpha(GREY[500], 0.24) : alpha('#9370DB', 0.2),
      hoverOpacity: isLight ? 0.08 : 0.12,
      disabledOpacity: 0.48,
    },
  };
}

// Export light mode palette by default (can be switched later)
const palette = createPalette('light');

export default palette;
